// authored by Joshua Beedle
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import IcomoonReact from 'icomoon-react';

import Wrapper from './Wrapper';
import { BoxShadow } from '../../GlobalStyles';
import Text from '../Text';
import iconSet from '../../images/teambuildr-selection.json';

const CustomButton = styled('button')`
  align-items: center;
  border: none;
  display: flex;
  justify-content: center;
  min-height: 39px;
  min-width: 100px;
`;

const DateSelectorContainer = styled('div')`
  border-radius: 6px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  display: flex;
  font-family: 'Nunito Sans';
  justify-content: center;
  flex: ${(props) => (props.rangeMode ? '1 1 auto' : 'none')};
  min-width: ${(props) => (props.rangeMode ? '120px' : 'auto')};

  .date-picker {
    border: none;
    font-weight: 400;
    height: 40px;
    width: 100%;
  }

  .react-datepicker {
    border: none;
    box-shadow: ${BoxShadow};
  }

  .react-datepicker-popper {
    z-index: 3;
  }

  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before {
    display: none;
  }

  .react-datepicker__day--has-data,
  .react-datepicker__day--has-data.react-datepicker__day--keyboard-selected,
  .react-datepicker__day--has-data.react-datepicker__day--selected {
    position: relative;
  }

  .react-datepicker__day--has-data::after {
    background-color: #FF6600;
    border-radius: 50%;
    bottom: -10px;
    content: '';
    height: 6px;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    width: 6px;
    z-index: 2;
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__month-text--in-range,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__year-text--in-range,
  .react-datepicker__year-text--in-selecting-range,
  .react-datepicker__year-text--selected {
    background-color: #FF6600;
    color: white;
  }

  .react-datepicker__day--inside-month {
    color: #444444;
  }

  .react-datepicker__day--keyboard-selected {
    background: none;
    color: #444444;
  }

  .react-datepicker__day--outside-month {
    color: #BDBDBD;
  }

  .react-datepicker__day--selected {
    background-color: #FF6600;
    color: white;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    margin-bottom: 15px;
  }

  .react-datepicker__header {
    background: white;
    border-bottom: none;
  }

  .react-datepicker__month-container {
    font-family: 'Nunito Sans';
    font-size: 15px;
    padding: 15px;
  }

  .react-datepicker__triangle {
    display: none;
  }
`;

const TextAndPickerRow = styled('div')`
  align-items: center;
  border: ${(props) => {
    if (props.customBorder) return props.customBorder;
    if (props.disabled) return '#E0E0E0 solid 1px';
    return '#979797 solid 1px';
  }};
  border-radius: 4px;
  display: flex;
  flex-wrap: ${(props) => (props.rangeMode ? 'wrap' : 'nowrap')};
  justify-content: flex-start;
  min-height: ${(props) => props.customHeight || '39px'};
  padding-left: 12px;
  padding-right: 12px;
  width: ${(props) => (props.fullWidth ? '100%' : 'fit-content')};

  ${(props) => (props.customBorder && !props.disabled)
    && `
    &:hover {
      border: 1px solid #C0C0C0;
    }
  `}
`;

const TextContainer2 = styled('div')`
  display: flex;
  margin-left: 15px;
  margin-right: 15px;
`;

/**
 *
 * @param {Bool} disabled boolean to determine whether or not the picker is disabled
 * @param {Date} endDate selected end date in the picker
 * @param {Date} maxDate maximum selected date range
 * @param {Date} minDate minimum selected date range
 * @param {String} placeholderText text to display before date selected
 * @param {Function} setEndDate function to select the end date
 * @param {Function} setStartDate function to select the start date
 * @param {Bool} singleDate boolean to determine if it is single date or range
 * @param {Date} startDate selected start date in the picker
 * @param {Object} styledWrapperProps custom props for Wrapper
 *
*/

const SharedDatePicker = ({
  disabled,
  datesWithData,
  endDate,
  fullWidth,
  maxDate,
  minDate,
  placeholderText,
  setEndDate,
  setStartDate,
  singleDate,
  startDate,
  styledWrapperProps,
}) => {
  const rangeMode = !singleDate;
  // eslint-disable-next-line react/prop-types
  const CustomStartDateButton = forwardRef(({ value, onClick }, ref) => (
    <CustomButton disabled={disabled} type='button' onClick={onClick} ref={ref}>
      <Text styledTextProps={{ color: '#979797', fontSize: 16 }}>{value || placeholderText}</Text>
    </CustomButton>
  ));

  // eslint-disable-next-line react/prop-types
  const CustomEndDateButton = forwardRef(({ value, onClick }, ref) => (
    <CustomButton disabled={disabled} type='button' onClick={onClick} ref={ref}>
      <Text styledTextProps={{ color: '#979797', fontSize: 16 }}>{value || 'End Date'}</Text>
    </CustomButton>
  ));

  const defaultMaxDate = maxDate || new Date();
  const defaultMinDate = minDate || startDate;

  return (
    singleDate ? (
      <Wrapper cursor={disabled ? 'default' : 'pointer'} {...styledWrapperProps}>
        <TextAndPickerRow
          disabled={disabled}
          customBorder={styledWrapperProps?.customBorder}
          customHeight={styledWrapperProps?.customHeight}
        >
          <DateSelectorContainer disabled={disabled}>
            <DatePicker
              name='shared-date-picker'
              title=''
              className='date-picker'
              dateFormat='MMM dd, yyyy'
              placeholderText={placeholderText}
              selected={startDate}
              popperPlacement='top-start'
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                },
              }}
              maxDate={defaultMaxDate}
              minDate={minDate || null}
              onChange={(date) => {
                setStartDate(date);
              }}
              customInput={<CustomStartDateButton placeholderText={placeholderText} />}
              startDate={startDate}
              endDate={endDate}
              dayClassName={(date) => {
                const dateStr = date.toISOString().split('T')[0];
                return datesWithData?.some((d) => d.toISOString().split('T')[0] === dateStr)
                  ? 'react-datepicker__day--has-data' : undefined;
              }}
              renderCustomHeader={({
                decreaseMonth,
                increaseMonth,
                date,
              }) => (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                }}
                >
                  <button
                    type='button'
                    onClick={decreaseMonth}
                    style={{
                      border: 'none',
                      marginLeft: '10px',
                      background: 'rgba(95, 96, 100, 0.05)',
                      borderRadius: '5px',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IcomoonReact
                      iconSet={iconSet}
                      size={15}
                      icon='left-arrow'
                      color='#FF6600'
                    />
                  </button>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontFamily: 'Nunito Sans',
                    fontWeight: '700',
                    fontSize: '16px',
                  }}
                  >
                    {date?.toLocaleString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  <button
                    type='button'
                    onClick={increaseMonth}
                    style={{
                      border: 'none',
                      marginRight: '10px',
                      background: 'rgba(95, 96, 100, 0.05)',
                      borderRadius: '5px',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IcomoonReact
                      iconSet={iconSet}
                      size={15}
                      icon='right-arrow'
                      color='#FF6600'
                    />
                  </button>
                </div>
              )}
            />
          </DateSelectorContainer>
        </TextAndPickerRow>
      </Wrapper>
    ) : (
      <Wrapper cursor={disabled ? 'default' : 'pointer'} {...styledWrapperProps}>
        <TextAndPickerRow
          disabled={disabled}
          rangeMode={rangeMode}
          fullWidth={fullWidth}
          customBorder={styledWrapperProps?.customBorder}
          customHeight={styledWrapperProps?.customHeight}
        >
          <DateSelectorContainer rangeMode={rangeMode}>
            <DatePicker
              name='shared-date-picker-start-date'
              title=''
              className='date-picker'
              dateFormat='MMM dd, yyyy'
              placeholderText='Start Date'
              selected={startDate}
              popperPlacement='top-start'
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                },
              }}
              maxDate={defaultMaxDate}
              onChange={(date) => {
                setStartDate(date);
              }}
              customInput={<CustomStartDateButton placeholderText={placeholderText} />}
              startDate={startDate}
              endDate={endDate}
              selectsStart='multiDay'
              renderCustomHeader={({
                decreaseMonth,
                increaseMonth,
                date,
              }) => (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                }}
                >
                  <button
                    type='button'
                    onClick={decreaseMonth}
                    style={{
                      border: 'none',
                      marginLeft: '10px',
                      background: 'rgba(95, 96, 100, 0.05)',
                      borderRadius: '5px',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IcomoonReact
                      iconSet={iconSet}
                      size={15}
                      icon='left-arrow'
                      color='#FF6600'
                    />
                  </button>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontFamily: 'Nunito Sans',
                    fontWeight: '700',
                    fontSize: '16px',
                  }}
                  >
                    {date?.toLocaleString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  <button
                    type='button'
                    onClick={increaseMonth}
                    style={{
                      border: 'none',
                      marginRight: '10px',
                      background: 'rgba(95, 96, 100, 0.05)',
                      borderRadius: '5px',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IcomoonReact
                      iconSet={iconSet}
                      size={15}
                      icon='right-arrow'
                      color='#FF6600'
                    />
                  </button>
                </div>
              )}
            />
          </DateSelectorContainer>
          <>
            <TextContainer2>
              <IcomoonReact
                iconSet={iconSet}
                size={14}
                icon='alt-right-arrow'
                color='#979797'
              />
            </TextContainer2>
            <DateSelectorContainer rangeMode={rangeMode}>
              <DatePicker
                name='shared-date-picker-end-date'
                className='date-picker'
                dateFormat='MMM dd, yyyy'
                placeholderText='End Date'
                selected={endDate}
                popperPlacement='top-start'
                popperModifiers={{
                  preventOverflow: {
                    enabled: true,
                  },
                }}
                maxDate={defaultMaxDate}
                onChange={(date) => {
                  setEndDate(date);
                }}
                customInput={<CustomEndDateButton />}
                startDate={startDate}
                endDate={endDate}
                selectsEnd
                minDate={defaultMinDate}
                renderCustomHeader={({
                  decreaseMonth,
                  increaseMonth,
                  date,
                }) => (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                  }}
                  >
                    <button
                      type='button'
                      onClick={decreaseMonth}
                      style={{
                        border: 'none',
                        marginLeft: '10px',
                        background: 'rgba(95, 96, 100, 0.05)',
                        borderRadius: '5px',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IcomoonReact
                        iconSet={iconSet}
                        size={15}
                        icon='left-arrow'
                        color='#FF6600'
                      />
                    </button>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontFamily: 'Nunito Sans',
                      fontWeight: '700',
                      fontSize: '16px',
                    }}
                    >
                      {date?.toLocaleString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                    <button
                      type='button'
                      onClick={increaseMonth}
                      style={{
                        border: 'none',
                        marginRight: '10px',
                        background: 'rgba(95, 96, 100, 0.05)',
                        borderRadius: '5px',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IcomoonReact
                        iconSet={iconSet}
                        size={15}
                        icon='right-arrow'
                        color='#FF6600'
                      />
                    </button>
                  </div>
                )}
              />
            </DateSelectorContainer>
          </>
        </TextAndPickerRow>
      </Wrapper>
    )
  );
};

SharedDatePicker.propTypes = {
  disabled: PropTypes.bool,
  datesWithData: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ])),
  endDate: PropTypes.instanceOf(Date),
  fullWidth: PropTypes.bool,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  placeholderText: PropTypes.string,
  setEndDate: PropTypes.func,
  setStartDate: PropTypes.func.isRequired,
  singleDate: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  styledWrapperProps: PropTypes.instanceOf(Object),
};

SharedDatePicker.defaultProps = {
  disabled: false,
  datesWithData: [],
  endDate: null,
  fullWidth: false,
  maxDate: null,
  minDate: null,
  placeholderText: 'Select Date',
  setEndDate: null,
  singleDate: true,
  startDate: null,
  styledWrapperProps: {},
};

export default SharedDatePicker;
